import { useRef, useState, useCallback, useEffect } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight } from 'lucide-react';

const HERO_BG = 'https://images.unsplash.com/photo-1772717592071-bc9747046458?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBtaW5pbWFsaXN0JTIwYXJjaGl0ZWN0dXJlJTIwZGFya3xlbnwwfHx8fDE3NzUzNDIwOTh8MA&ixlib=rb-4.1.0&q=85';
const HERO_VIDEO = 'https://videos.pexels.com/video-files/30590253/13098873_2560_1440_30fps.mp4';

export default function Hero() {
  const ref = useRef(null);
  const videoRef = useRef(null);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [spotlightPos, setSpotlightPos] = useState({ x: 50, y: 50 });
  const [spotlightActive, setSpotlightActive] = useState(false);

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start start', 'end start'],
  });
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
  const contentY = useTransform(scrollYProgress, [0, 1], ['0%', '15%']);

  const handleMouseMove = useCallback((e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setSpotlightPos({ x, y });
  }, []);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = true;
      videoRef.current.play().catch(() => {});
    }
  }, []);

  return (
    <section
      ref={ref}
      className="hero-section"
      id="top"
      data-testid="hero-section"
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setSpotlightActive(true)}
      onMouseLeave={() => setSpotlightActive(false)}
    >
      <div
        className="hero-bg"
        style={{
          backgroundImage: `url(${HERO_BG})`,
          opacity: videoLoaded ? 0 : 1,
          transition: 'opacity 1.5s ease',
        }}
      />

      <div className="hero-video-wrapper" data-testid="hero-video-wrapper">
        <video
          ref={videoRef}
          className="hero-video"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          poster={HERO_BG}
          onCanPlay={() => setVideoLoaded(true)}
          data-testid="hero-video"
        >
          <source src={HERO_VIDEO} type="video/mp4" />
        </video>
      </div>

      <div className="hero-overlay" />

      <div
        className="hero-spotlight"
        data-testid="hero-spotlight"
        style={{
          opacity: spotlightActive ? 1 : 0,
          background: `radial-gradient(600px circle at ${spotlightPos.x}% ${spotlightPos.y}%, rgba(255,255,255,0.06) 0%, transparent 60%)`,
        }}
      />

      <div className="hero-vignette" />

      <motion.div className="hero-content" style={{ opacity, y: contentY }}>
        <motion.p
          className="section-number mb-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.8 }}
          data-testid="hero-label"
        >
          Civic Ventures
        </motion.p>

        <motion.h1
          className="hero-headline"
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 1, ease: [0.16, 1, 0.3, 1] }}
          data-testid="hero-headline"
        >
          Some Ideas
          <br />
          Deserve
          <span className="accent-line">Real-World Form.</span>
        </motion.h1>

        <motion.p
          className="hero-sub"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          data-testid="hero-subtext"
        >
          A creative AI venture studio helping ambitious people
          turn ideas, business challenges, and bold concepts
          into tangible systems, visuals, and next steps.
        </motion.p>

        <motion.a
          href="#contact"
          className="hero-cta"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          data-testid="hero-cta"
        >
          Start the Conversation
          <ArrowRight size={16} strokeWidth={2} />
        </motion.a>
      </motion.div>

      <motion.div
        className="scroll-indicator"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 0.6 }}
      >
        <span className="scroll-text">Scroll</span>
        <div className="scroll-line" />
      </motion.div>
    </section>
  );
}
